import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import DateCapacityIndicator from './DateCapacityIndicator';
import DailyBookingBreakdown from './DailyBookingBreakdown';

interface DaySummary {
  date: string;
  day_of_week: string;
  is_closed: boolean;
  day_schedule?: {
    closed: boolean;
    capacity: number;
    time_slots?: {
      morning?: { enabled: boolean; start_time: string; end_time: string; capacity: number };
      afternoon?: { enabled: boolean; start_time: string; end_time: string; capacity: number };
      evening?: { enabled: boolean; start_time: string; end_time: string; capacity: number };
    };
  };
  capacity: {
    max_capacity: number;
    total_bookings: number;
    confirmed: number;
    pending: number;
    waitlisted: number;
    checked_in: number;
    checked_out: number;
    cancelled: number;
    no_show: number;
    active_bookings: number;
    available_spots: number;
    utilization_percent: number;
  };
  bookings?: any[];
}

interface WeeklyCapacityViewProps {
  summaryData: DaySummary[];
  loading?: boolean;
  error?: string | null;
  onDateClick?: (date: string) => void;
  isMonthView?: boolean;
  expandedDate?: string | null;
  expandedDayData?: DaySummary | null;
  loadingExpanded?: boolean;
  onConfirmBooking?: (bookingId: string) => void;
  onCancelBooking?: (bookingId: string) => void;
  onReinstateBooking?: (bookingId: string) => void;
  onCheckIn?: (bookingId: string) => void;
  onCheckOut?: (checkInId: string) => void;
  onAddBooking?: (date: string) => void;
  onEditBooking?: (booking: any) => void;
}

/**
 * Weekly capacity overview component
 * Shows 7-day grid with capacity indicators for each day
 * Supports inline expansion of daily booking details
 */
const WeeklyCapacityView: React.FC<WeeklyCapacityViewProps> = ({
  summaryData,
  loading = false,
  error = null,
  onDateClick,
  isMonthView = false,
  expandedDate = null,
  expandedDayData = null,
  loadingExpanded = false,
  onConfirmBooking,
  onCancelBooking,
  onReinstateBooking,
  onCheckIn,
  onCheckOut,
  onAddBooking,
  onEditBooking,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // < 600px (mobile - 1 column)
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-960px (tablet - 2 columns)
  const isMdUp = useMediaQuery(theme.breakpoints.up('md')); // >= 960px (desktop - 7 columns)

  const formatDate = (dateString: string) => {
    // Parse date as local time to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isToday = (dateString: string) => {
    const today = new Date();
    // Parse date as local time to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const checkDate = new Date(year, month - 1, day);
    return (
      today.getFullYear() === checkDate.getFullYear() &&
      today.getMonth() === checkDate.getMonth() &&
      today.getDate() === checkDate.getDate()
    );
  };

  const isPastDate = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
    // Parse date as local time to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const checkDate = new Date(year, month - 1, day);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (summaryData.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No data available for the selected period
      </Alert>
    );
  }

  // For month view, calculate the starting day of week (0 = Sunday, 6 = Saturday)
  const getStartingDayOfWeek = (): number => {
    if (!isMonthView || summaryData.length === 0) return 0;
    const [year, month, day] = summaryData[0].date.split('-').map(Number);
    const firstDate = new Date(year, month - 1, day);
    return firstDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
  };

  const startingDayOfWeek = getStartingDayOfWeek();
  const emptySlots = isMonthView ? startingDayOfWeek : 0;

  // Helper to determine if we should render the expanded section after this index
  // Adapts to different screen sizes:
  // - Mobile (xs): Show immediately after clicked card
  // - Tablet (sm): Show at end of row (2 columns)
  // - Desktop (md+): Show at end of row (7 columns)
  const shouldRenderExpandedAfter = (currentIndex: number): boolean => {
    if (!expandedDate) return false;

    // Find which data index has the expanded date
    const expandedDataIndex = summaryData.findIndex(day => day.date === expandedDate);
    if (expandedDataIndex === -1) return false;

    // Determine columns per row based on screen size
    let columnsPerRow: number;
    if (isXs) {
      // Mobile: 1 column - show immediately after clicked card
      return currentIndex === expandedDataIndex;
    } else if (isSm) {
      // Tablet: 2 columns per row (no empty slots in mobile/tablet)
      columnsPerRow = 2;
    } else {
      // Desktop: 7 columns per row
      columnsPerRow = 7;
    }

    // Calculate row-based positioning for tablet and desktop
    // Only include empty slots in grid calculation for desktop (md+)
    const emptySlotsForCalc = isMdUp ? emptySlots : 0;
    const expandedGridIndex = expandedDataIndex + emptySlotsForCalc;
    const currentGridIndex = currentIndex + emptySlotsForCalc;

    // Calculate which row the expanded card is in
    const expandedRow = Math.floor(expandedGridIndex / columnsPerRow);

    // Find the last index in that row
    const lastIndexInRow = (expandedRow + 1) * columnsPerRow - 1;

    // Check if current index is the last in the expanded card's row
    // OR if it's the last card in the data (for partial rows)
    const isLastInData = currentIndex === summaryData.length - 1;
    const isLastInExpandedRow = currentGridIndex === lastIndexInRow;

    // Also check if the expanded card is in the current or previous position within the same row
    const isExpandedInCurrentRow = Math.floor(currentGridIndex / columnsPerRow) === expandedRow;

    return (isLastInExpandedRow || (isLastInData && isExpandedInCurrentRow));
  };

  return (
    <Grid container spacing={2}>
      {/* Add empty slots for month view to align with Sun-Sat calendar */}
      {/* Only show empty slots on desktop (md+) where 7-column calendar layout is used */}
      {isMonthView && isMdUp && Array.from({ length: emptySlots }).map((_, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 12/7 }} key={`empty-${index}`}>
          <Box sx={{ height: '100%', minHeight: 200 }} />
        </Grid>
      ))}

      {summaryData.map((day, index) => {
        const today = isToday(day.date);
        const isPast = isPastDate(day.date);
        const isExpanded = expandedDate === day.date;

        return (
          <React.Fragment key={day.date}>
            {/* Day Card */}
            <Grid size={{ xs: 12, sm: 6, md: 12/7 }}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: onDateClick ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  border: isExpanded ? 2 : today ? 2 : 1,
                  borderColor: isExpanded ? 'secondary.main' : today ? 'primary.main' : 'divider',
                  backgroundColor: isPast ? 'action.disabledBackground' : 'background.paper',
                  opacity: isPast ? 0.7 : 1,
                  '&:hover': onDateClick
                    ? {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                      }
                    : {},
                }}
                onClick={() => onDateClick && onDateClick(day.date)}
              >
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Header */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '0.9rem', lg: '1.25rem' },
                        fontWeight: 600,
                      }}
                    >
                      {day.day_of_week}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { md: '0.75rem', lg: '0.875rem' } }}
                    >
                      {formatDate(day.date)}
                    </Typography>
                  </Box>

                  {/* Capacity Indicator */}
                  <Box sx={{ mb: 2 }}>
                    <DateCapacityIndicator
                      maxCapacity={day.capacity.max_capacity}
                      activeBookings={day.capacity.active_bookings}
                      availableSpots={day.capacity.available_spots}
                      isClosed={day.is_closed}
                      size="medium"
                      showLabel={false}
                    />
                  </Box>

                  {/* Stats */}
                  {!day.is_closed && (
                    <Box>
                      {/* Time Slot Breakdown */}
                      {day.day_schedule?.time_slots && (
                        <>
                          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                            {day.day_schedule.time_slots.morning?.enabled && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { md: '0.65rem', lg: '0.75rem' } }}>
                                  Morning:
                                </Typography>
                                <Chip
                                  label={`${day.bookings?.filter(b => {
                                    const startTime = b.start_time;
                                    const isActive = !['cancelled', 'no_show', 'waitlisted'].includes(b.status.toLowerCase());
                                    return isActive &&
                                           startTime >= day.day_schedule!.time_slots!.morning!.start_time &&
                                           startTime < day.day_schedule!.time_slots!.morning!.end_time;
                                  }).length || 0}/${day.day_schedule.time_slots.morning.capacity}`}
                                  size="small"
                                  sx={{ height: 18, fontSize: { md: '0.65rem', lg: '0.7rem' } }}
                                />
                              </Box>
                            )}
                            {day.day_schedule.time_slots.afternoon?.enabled && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { md: '0.65rem', lg: '0.75rem' } }}>
                                  Afternoon:
                                </Typography>
                                <Chip
                                  label={`${day.bookings?.filter(b => {
                                    const startTime = b.start_time;
                                    const isActive = !['cancelled', 'no_show', 'waitlisted'].includes(b.status.toLowerCase());
                                    return isActive &&
                                           startTime >= day.day_schedule!.time_slots!.afternoon!.start_time &&
                                           startTime < day.day_schedule!.time_slots!.afternoon!.end_time;
                                  }).length || 0}/${day.day_schedule.time_slots.afternoon.capacity}`}
                                  size="small"
                                  sx={{ height: 18, fontSize: { md: '0.65rem', lg: '0.7rem' } }}
                                />
                              </Box>
                            )}
                            {day.day_schedule.time_slots.evening?.enabled && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { md: '0.65rem', lg: '0.75rem' } }}>
                                  Evening:
                                </Typography>
                                <Chip
                                  label={`${day.bookings?.filter(b => {
                                    const startTime = b.start_time;
                                    const isActive = !['cancelled', 'no_show', 'waitlisted'].includes(b.status.toLowerCase());
                                    return isActive &&
                                           startTime >= day.day_schedule!.time_slots!.evening!.start_time &&
                                           startTime < day.day_schedule!.time_slots!.evening!.end_time;
                                  }).length || 0}/${day.day_schedule.time_slots.evening.capacity}`}
                                  size="small"
                                  sx={{ height: 18, fontSize: { md: '0.65rem', lg: '0.7rem' } }}
                                />
                              </Box>
                            )}
                          </Stack>
                          <Divider sx={{ my: 1 }} />
                        </>
                      )}

                      {/* Action Stats */}
                      <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { md: '0.7rem', lg: '0.875rem' } }}
                          >
                            Pending:
                          </Typography>
                          <Chip
                            label={day.capacity.pending || 0}
                            size="small"
                            color={day.capacity.pending > 0 ? 'warning' : 'default'}
                            sx={{ height: 20, fontSize: { md: '0.7rem', lg: '0.75rem' }, minWidth: 30 }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { md: '0.7rem', lg: '0.875rem' } }}
                          >
                            Waitlist:
                          </Typography>
                          <Chip
                            label={day.capacity.waitlisted || 0}
                            size="small"
                            color={day.capacity.waitlisted > 0 ? 'info' : 'default'}
                            sx={{ height: 20, fontSize: { md: '0.7rem', lg: '0.75rem' }, minWidth: 30 }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { md: '0.7rem', lg: '0.875rem' } }}
                          >
                            Canceled:
                          </Typography>
                          <Chip
                            label={day.capacity.cancelled || 0}
                            size="small"
                            color={day.capacity.cancelled > 0 ? 'error' : 'default'}
                            sx={{ height: 20, fontSize: { md: '0.7rem', lg: '0.75rem' }, minWidth: 30 }}
                          />
                        </Box>
                      </Stack>
                    </Box>
                  )}

                  {day.is_closed && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { md: '0.7rem', lg: '0.875rem' } }}
                    >
                      Closed
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Expanded Booking Details - Full Width Row (position adapts to screen size) */}
            {shouldRenderExpandedAfter(index) && (
              <Grid size={{ xs: 12 }}>
                <Fade in={true} timeout={300}>
                  <Box sx={{ px: 2, py: 1 }}>
                    {loadingExpanded ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : expandedDayData ? (
                      <Box sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        p: 2,
                        boxShadow: 2,
                      }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                          Booking Details for {expandedDayData.day_of_week}, {formatDate(expandedDayData.date)}
                        </Typography>
                        <DailyBookingBreakdown
                          date={expandedDayData.date}
                          dayOfWeek={expandedDayData.day_of_week}
                          isClosed={expandedDayData.is_closed}
                          capacity={expandedDayData.capacity}
                          bookings={expandedDayData.bookings || []}
                          daySchedule={expandedDayData.day_schedule}
                          defaultExpanded={true}
                          onConfirmBooking={onConfirmBooking}
                          onCancelBooking={onCancelBooking}
                          onReinstateBooking={onReinstateBooking}
                          onCheckIn={onCheckIn}
                          onCheckOut={onCheckOut}
                          onAddBooking={onAddBooking}
                          onEditBooking={onEditBooking}
                        />
                      </Box>
                    ) : (
                      <Alert severity="info">
                        Click on a day to see detailed booking information
                      </Alert>
                    )}
                  </Box>
                </Fade>
              </Grid>
            )}
          </React.Fragment>
        );
      })}
    </Grid>
  );
};

export default WeeklyCapacityView;
